import {
  useCustomPageArbitraryFilterFields,
  useCustomPageBrand,
  useCustomPageSubject,
  useCustomPageUser,
  useCustomPageDispatchAction,
  useSetCustomPageArbitraryFilterFields,
} from '../contexts/default.js';
import { useState, type FormEvent, type ReactElement } from 'react';

type SubjectFormState = {
  id: string;
  name: string;
  kind: string;
};

const getInitialFormState = (subject: ReturnType<typeof useCustomPageSubject>): SubjectFormState => ({
  id: subject ? String(subject.id) : '',
  name: subject?.name ?? '',
  kind: subject?.kind ?? '',
});

export const TestCustomPage = (): ReactElement => {
  const user = useCustomPageUser();
  const brand = useCustomPageBrand();
  const subject = useCustomPageSubject();
  const arbitraryFilterFields = useCustomPageArbitraryFilterFields();
  const setArbitraryFilterFields = useSetCustomPageArbitraryFilterFields();
  const dispatchAction = useCustomPageDispatchAction();

  const [form, setForm] = useState<SubjectFormState>(() => getInitialFormState(subject));
  const [fieldKey, setFieldKey] = useState('');
  const [fieldValue, setFieldValue] = useState('');

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const id = form.id.trim();
    const name = form.name.trim();

    if (!id || !name) return;

    dispatchAction({
      type: 'update-subject',
      payload: {
        subject: {
          id,
          name,
          ...(form.kind.trim() ? { kind: form.kind.trim() } : {}),
        },
      },
    });
  };

  const onArbitraryFieldSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    const key = fieldKey.trim();
    if (!key) return;

    setArbitraryFilterFields({
      ...arbitraryFilterFields,
      [key]: fieldValue,
    });

    setFieldKey('');
    setFieldValue('');
  };

  return (
    <section style={{ display: 'grid', gap: 16, padding: 16 }}>
      <h2 style={{ margin: 0 }}>Custom Page Test</h2>

      <div>
        <h3 style={{ margin: '0 0 8px' }}>User</h3>
        <pre style={{ margin: 0 }}>{JSON.stringify(user, null, 2)}</pre>
      </div>

      <div>
        <h3 style={{ margin: '0 0 8px' }}>Brand</h3>
        <pre style={{ margin: 0 }}>{JSON.stringify(brand, null, 2)}</pre>
      </div>

      <div>
        <h3 style={{ margin: '0 0 8px' }}>Current Subject</h3>
        <pre style={{ margin: 0 }}>{JSON.stringify(subject, null, 2)}</pre>
      </div>

      <div>
        <h3 style={{ margin: '0 0 8px' }}>Arbitrary Filter Fields</h3>
        <pre style={{ margin: 0 }}>{JSON.stringify(arbitraryFilterFields, null, 2)}</pre>
      </div>

      <form onSubmit={onSubmit} style={{ display: 'grid', gap: 8, maxWidth: 420 }}>
        <h3 style={{ margin: 0 }}>Update Subject</h3>

        <label style={{ display: 'grid', gap: 4 }}>
          <span>Subject ID</span>
          <input
            value={form.id}
            onChange={(event) => setForm((prev) => ({ ...prev, id: event.target.value }))}
            placeholder="Subject id"
          />
        </label>

        <label style={{ display: 'grid', gap: 4 }}>
          <span>Subject Name</span>
          <input
            value={form.name}
            onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
            placeholder="Subject name"
          />
        </label>

        <label style={{ display: 'grid', gap: 4 }}>
          <span>Subject Kind (optional)</span>
          <input
            value={form.kind}
            onChange={(event) => setForm((prev) => ({ ...prev, kind: event.target.value }))}
            placeholder="hcp"
          />
        </label>

        <button type="submit">Dispatch update-subject</button>
      </form>

      <form onSubmit={onArbitraryFieldSubmit} style={{ display: 'grid', gap: 8, maxWidth: 420 }}>
        <h3 style={{ margin: 0 }}>Set Arbitrary Filter Field</h3>

        <label style={{ display: 'grid', gap: 4 }}>
          <span>Field Key</span>
          <input
            value={fieldKey}
            onChange={(event) => setFieldKey(event.target.value)}
            placeholder="customFilterKey"
          />
        </label>

        <label style={{ display: 'grid', gap: 4 }}>
          <span>Field Value</span>
          <input
            value={fieldValue}
            onChange={(event) => setFieldValue(event.target.value)}
            placeholder="custom value"
          />
        </label>

        <button type="submit">Set field on filters</button>
      </form>
    </section>
  );
};
